export type SecurityClearanceCheckMessageKind =
  | "approved"
  | "rejected_suspended"
  | "not_found"
  | "other_status";

export interface SecurityClearanceStatusCheckResponse {
  isPassportOrEmiratesIdNumberDuplicate?: boolean;
  status?: string | null;
  expirationDate?: string | null;
}

export interface SecurityClearanceCheckResult {
  kind: SecurityClearanceCheckMessageKind;
  status?: string;
}

const REJECTED_OR_SUSPENDED_STATUSES = new Set<string>([
  "REJECTED",
  "SUSPENDED",
  "PERMANENTLY_REJECTED",
]);

export const resolveSecurityClearanceCheckResult = (
  response: SecurityClearanceStatusCheckResponse | null | undefined,
): SecurityClearanceCheckResult => {
  if (!response) {
    return { kind: "not_found" };
  }

  const status = String(response.status ?? "")
    .trim()
    .toUpperCase();

  // 1. Approved and valid
  if (status === "APPROVED") {
    return { kind: "approved", status };
  }

  // 2. Rejected / suspended
  if (REJECTED_OR_SUSPENDED_STATUSES.has(status)) {
    return { kind: "rejected_suspended", status };
  }

  // 3. Not found — no record / no status returned
  if (!status) {
    if (response.isPassportOrEmiratesIdNumberDuplicate === false) {
      return { kind: "not_found" };
    }

    if (response.isPassportOrEmiratesIdNumberDuplicate === true) {
      return { kind: "other_status", status: "UNKNOWN" };
    }

    return { kind: "not_found" };
  }

  // 4. Any other submission status (pending, expired, etc.)
  return { kind: "other_status", status };
};

export const formatSecurityClearanceExpirationDate = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

export const getSecurityClearanceStatusLabelKey = (status: string) =>
  `security-clearance.status-labels.${status}`;
